/**
 * Curl 命令词法分析器
 * 负责将 curl 命令字符串分解为 token
 */

export class CurlTokenizer {
  constructor() {
    this.position = 0;
    this.command = '';
    this.tokens = [];
  }

  /**
   * 分析 curl 命令
   * @param {string} command - curl 命令字符串
   * @returns {Array} token 数组
   */
  tokenize(command) {
    this.reset();
    this.command = this.preprocessCommand(command);

    while (this.position < this.command.length) {
      this.skipWhitespace();

      if (this.position >= this.command.length) {
        break;
      }

      const char = this.command[this.position];

      if (char === '-') {
        this.tokenizeOption();
      } else if (this.isQuote(char)) {
        this.tokenizeQuotedString();
      } else if (char === '\\') {
        this.tokenizeEscapedCharacter();
      } else {
        this.tokenizeString();
      }
    }

    return this.tokens;
  }

  /**
   * 重置分析器状态
   */
  reset() {
    this.position = 0;
    this.command = '';
    this.tokens = [];
  }

  /**
   * 预处理命令字符串
   * @param {string} command - 原始命令
   * @returns {string} 处理后的命令
   */
  preprocessCommand(command) {
    if (!command || typeof command !== 'string') {
      return '';
    }

    return command
      // 处理换行符
      .replace(/\\\n/g, ' ')
      .replace(/\\\r\n/g, ' ')
      // 合并多个空格
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * 跳过空白字符
   */
  skipWhitespace() {
    while (this.position < this.command.length &&
           this.isWhitespace(this.command[this.position])) {
      this.position++;
    }
  }

  /**
   * 检查是否为空白字符
   * @param {string} char - 字符
   * @returns {boolean} 是否为空白字符
   */
  isWhitespace(char) {
    return /\s/.test(char);
  }

  /**
   * 检查是否为引号
   * @param {string} char - 字符
   * @returns {boolean} 是否为引号
   */
  isQuote(char) {
    return char === '"' || char === "'";
  }

  /**
   * 分析选项 token
   */
  tokenizeOption() {
    const start = this.position;
    this.position++; // 跳过第一个 '-'

    // 检查是否为长选项
    if (this.position < this.command.length && this.command[this.position] === '-') {
      this.position++; // 跳过第二个 '-'
      this.tokenizeLongOption(start);
    } else {
      this.tokenizeShortOption(start);
    }
  }

  /**
   * 分析短选项（如 -X, -H）
   * @param {number} start - 开始位置
   */
  tokenizeShortOption(start) {
    let value = '-';

    // 收集选项字符
    while (this.position < this.command.length &&
           !this.isWhitespace(this.command[this.position]) &&
           this.command[this.position] !== '=') {
      value += this.command[this.position];
      this.position++;
    }

    this.tokens.push({
      type: 'option',
      value: value,
      start: start,
      end: this.position
    });
  }

  /**
   * 分析长选项（如 --header, --data）
   * @param {number} start - 开始位置
   */
  tokenizeLongOption(start) {
    let value = '--';

    // 收集选项名称
    while (this.position < this.command.length &&
           !this.isWhitespace(this.command[this.position]) &&
           this.command[this.position] !== '=') {
      value += this.command[this.position];
      this.position++;
    }

    this.tokens.push({
      type: 'option',
      value: value,
      start: start,
      end: this.position
    });
  }

  /**
   * 分析引号字符串
   */
  tokenizeQuotedString() {
    const quote = this.command[this.position];
    const start = this.position;
    this.position++; // 跳过开始引号

    let value = '';
    let escaped = false;

    while (this.position < this.command.length) {
      const char = this.command[this.position];

      if (escaped) {
        // 处理转义字符
        value += this.unescapeCharacter(char);
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === quote) {
        // 找到结束引号
        this.position++;
        break;
      } else {
        value += char;
      }

      this.position++;
    }

    this.tokens.push({
      type: 'string',
      value: value,
      quoted: true,
      quote: quote,
      start: start,
      end: this.position
    });
  }

  /**
   * 分析转义字符
   */
  tokenizeEscapedCharacter() {
    const start = this.position;
    this.position++; // 跳过 '\'

    if (this.position >= this.command.length) {
      // 不完整的转义序列
      this.tokens.push({
        type: 'string',
        value: '\\',
        start: start,
        end: this.position
      });
      return;
    }

    const char = this.command[this.position];
    const unescaped = this.unescapeCharacter(char);
    this.position++;

    this.tokens.push({
      type: 'string',
      value: unescaped,
      escaped: true,
      original: '\\' + char,
      start: start,
      end: this.position
    });
  }

  /**
   * 分析普通字符串
   */
  tokenizeString() {
    const start = this.position;
    let value = '';

    while (this.position < this.command.length &&
           !this.isWhitespace(this.command[this.position])) {
      value += this.command[this.position];
      this.position++;
    }

    // 判断字符串类型
    const type = this.inferStringType(value);

    this.tokens.push({
      type: type,
      value: value,
      start: start,
      end: this.position
    });
  }

  /**
   * 推断字符串类型
   * @param {string} value - 字符串值
   * @returns {string} token 类型
   */
  inferStringType(value) {
    // 检查是否为 URL
    if (this.isUrl(value)) {
      return 'url';
    }

    // 检查是否为数字
    if (/^\d+$/.test(value)) {
      return 'number';
    }

    // 检查是否为布尔值
    if (value === 'true' || value === 'false') {
      return 'boolean';
    }

    return 'string';
  }

  /**
   * 检查是否为 URL
   * @param {string} str - 字符串
   * @returns {boolean} 是否为 URL
   */
  isUrl(str) {
    try {
      new URL(str);
      return true;
    } catch {
      // 检查相对 URL 模式
      return /^https?:\/\//i.test(str) ||
             /^\/\//.test(str) ||
             /^\/[^\/\s]/.test(str);
    }
  }

  /**
   * 反转义字符
   * @param {string} char - 转义字符
   * @returns {string} 反转义后的字符
   */
  unescapeCharacter(char) {
    const escapeMap = {
      'n': '\n',
      'r': '\r',
      't': '\t',
      'b': '\b',
      'f': '\f',
      'v': '\v',
      '\\': '\\',
      '"': '"',
      "'": "'"
    };

    return escapeMap[char] || char;
  }

  /**
   * 获取当前位置的字符
   * @returns {string|null} 当前字符或 null
   */
  peek() {
    return this.position < this.command.length ?
           this.command[this.position] : null;
  }

  /**
   * 获取下一个字符（不移动位置）
   * @returns {string|null} 下一个字符或 null
   */
  peekNext() {
    return this.position + 1 < this.command.length ?
           this.command[this.position + 1] : null;
  }

  /**
   * 消费字符
   * @returns {string|null} 被消费的字符或 null
   */
  consume() {
    return this.position < this.command.length ?
           this.command[this.position++] : null;
  }

  /**
   * 检查是否还有更多字符
   * @returns {boolean} 是否还有更多字符
   */
  hasMore() {
    return this.position < this.command.length;
  }

  /**
   * 获取剩余的字符串
   * @returns {string} 剩余字符串
   */
  remaining() {
    return this.command.substring(this.position);
  }

  /**
   * 获取分析结果的调试信息
   * @returns {Object} 调试信息
   */
  getDebugInfo() {
    return {
      command: this.command,
      position: this.position,
      tokens: this.tokens,
      remaining: this.remaining()
    };
  }
}